import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, Send, Mail, MessageCircle, CheckCircle, XCircle, FileText, Clock, User, MapPin, Calendar, Package, Phone, Building, CreditCard, Truck, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import WorkflowTimeline from '../../components/shared/WorkflowTimeline';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

const SalesOrderDetailPage = () => {
  const { businessId, orderId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && orderId) {
      fetchOrder();
    }
  }, [bid, orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sales-orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load order'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setConfirmModal({
      title: 'Confirm Sales Order',
      description: 'Confirm this order? Inventory will be reserved.',
      confirmLabel: 'Confirm Order',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      icon: CheckCircle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/sales-orders/${orderId}/confirm`);
          toast.success('Order confirmed');
          fetchOrder();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm'));
        }
      }
    });
  };

  const handleCancel = () => {
    setConfirmModal({
      title: 'Cancel Sales Order',
      description: 'Order will be voided and reserved inventory released. This cannot be undone.',
      confirmLabel: 'Cancel Order',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      icon: XCircle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          // Backend accepts `reason` as query param but never persists it;
          // no UI collection needed.
          await api.patch(`/sales-orders/${orderId}/cancel`);
          toast.success('Order cancelled');
          fetchOrder();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel order'));
        }
      }
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/sales-orders/${orderId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SalesOrder_${order?.order_number || orderId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error downloading PDF'));
    }
  };

  // Email + WhatsApp are stub integrations on the backend -- they log the
  // intent into activity_log and return a message + note explaining that
  // delivery integration is pending. Show the backend's note as a toast so
  // users know the button worked AND that nothing was actually sent yet.
  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await api.post(`/sales-orders/${orderId}/send-email`);
      const { message, note } = response.data || {};
      toast.success(message || 'Email queued');
      if (note) toast(note, { icon: 'ℹ️', duration: 4500 });
      fetchOrder();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const response = await api.post(`/sales-orders/${orderId}/send-whatsapp`);
      const { message, note } = response.data || {};
      toast.success(message || 'WhatsApp queued');
      if (note) toast(note, { icon: 'ℹ️', duration: 4500 });
      fetchOrder();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send WhatsApp'));
    }
  };

  const handleConvertToInvoice = () => {
    setConfirmModal({
      title: 'Convert to Sales Invoice',
      description: 'Convert this order to a Sales Invoice?',
      confirmLabel: 'Convert',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      icon: FileText,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setConverting(true);
          const response = await api.post(`/sales-orders/${orderId}/convert-to-invoice`);
          toast.success(response.data.message || 'Converted to invoice successfully');
          fetchOrder();
          if (response.data.invoice?.id) {
            navigate(`/business/${bid}/sales/invoices/${response.data.invoice.id}`);
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert to invoice'));
        } finally {
          setConverting(false);
        }
      }
    });
  };

  const handleConvertToDeliveryChallan = () => {
    setConfirmModal({
      title: 'Convert to Delivery Challan',
      description: 'Convert this order to a Delivery Challan?',
      confirmLabel: 'Convert',
      confirmClass: 'bg-purple-600 hover:bg-purple-700',
      iconClass: 'text-purple-600',
      iconBgClass: 'bg-purple-100',
      icon: Truck,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setConverting(true);
          const response = await api.post(`/sales-orders/${orderId}/convert-to-delivery-challan`);
          toast.success(response.data.message || 'Converted to delivery challan successfully');
          fetchOrder();
          if (response.data.delivery_challan?.id) {
            navigate(`/business/${bid}/delivery-challans/${response.data.delivery_challan.id}`);
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert to delivery challan'));
        } finally {
          setConverting(false);
        }
      }
    });
  };

  // Smooth navigation with transition
  const handleBack = () => {
    navigate(`/business/${bid}/sales-orders`);
  };

  // Prepare linked documents for WorkflowTimeline
  const getLinkedDocuments = () => {
    if (!order) return {};
    return {
      quotation: order.source_quotation_id ? { id: order.source_quotation_id, number: order.source_quotation_number } : null,
      salesOrder: { id: order.id, number: order.order_number },
      deliveryChallan: order.converted_challan_id ? { id: order.converted_challan_id, number: order.converted_challan_number } : null,
      invoice: order.converted_invoice_id ? { id: order.converted_invoice_id, number: order.converted_invoice_number } : null,
    };
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Order not found</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:underline text-[14px]">
            ← Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Draft' },
    CONFIRMED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Confirmed' },
    CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Cancelled' },
    CLOSED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Closed' },
  };

  const statusBadge = statusConfig[order.status] || statusConfig.DRAFT;
  const StatusIcon = statusBadge.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Order Info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{order.order_number}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusBadge.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Created on {formatDate(order.created_at)} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {order.status === 'DRAFT' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/sales-orders/${orderId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Order
                  </button>
                  <button
                    onClick={handleConfirm}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirm Order
                  </button>
                </>
              )}
              {order.status === 'CONFIRMED' && (
                <>
                  {!order.converted_challan_id && (
                    <button
                      onClick={handleConvertToDeliveryChallan}
                      disabled={converting}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98] disabled:opacity-50"
                    >
                      <Truck className="w-4 h-4" />
                      {converting ? 'Converting...' : 'Create Challan'}
                    </button>
                  )}
                  {!order.converted_invoice_id && (
                    <button
                      onClick={handleConvertToInvoice}
                      disabled={converting}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-blue-600/20 active:scale-[0.98] disabled:opacity-50"
                    >
                      <FileText className="w-4 h-4" />
                      {converting ? 'Converting...' : 'Create Invoice'}
                    </button>
                  )}
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel
                  </button>
                </>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button
                onClick={handleDownloadPdf}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send Email"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSendWhatsApp}
                className="inline-flex items-center justify-center w-10 h-10 text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all duration-200 active:scale-[0.98]"
                title="Send WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT - Premium 3-Column Layout
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          
          {/* Workflow Timeline Card */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-5">
            <WorkflowTimeline
              currentDocument={{ type: 'SALES_ORDER', id: order.id, number: order.order_number }}
              linkedDocuments={getLinkedDocuments()}
              businessId={bid}
              compact={false}
            />
          </div>

          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Customer & Shipping (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Customer Card - Premium */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Customer</h2>
                </div>
                
                {order.customer_name || order.party_name ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-blue-700">
                          {(order.customer_name || order.party_name)?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{order.customer_name || order.party_name}</p>
                        <p className="text-[12px] text-slate-500">Customer</p>
                      </div>
                    </div>
                    
                    {(order.customer_phone || order.customer_mobile) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {order.customer_phone || order.customer_mobile}
                      </div>
                    )}
                    
                    {order.customer_email && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {order.customer_email}
                      </div>
                    )}
                    
                    {order.customer_gstin && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building className="w-4 h-4 text-slate-400" />
                        GSTIN: {order.customer_gstin}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
                      <User className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-[13px] text-slate-500">No customer selected</p>
                  </div>
                )}
              </div>

              {/* Shipping Card - Premium */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Truck className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Shipping Details</h2>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Expected Shipment</p>
                    <p className="text-[14px] font-medium text-slate-700">
                      {order.expected_shipment_date ? formatDate(order.expected_shipment_date) : '—'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Delivery Method</p>
                    <p className="text-[14px] font-medium text-slate-700">
                      {order.delivery_method || 'Standard Delivery'}
                    </p>
                  </div>
                  
                  {order.shipping_address && (
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Shipping Address</p>
                      <p className="text-[13px] text-slate-600 leading-relaxed">{order.shipping_address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Order Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Order Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(order.order_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Reference</span>
                    <span className="text-[13px] font-medium text-slate-700">{order.reference_number || '—'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Payment Terms</span>
                    <span className="text-[13px] font-medium text-slate-700">{order.payment_terms || 'Net 30'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Place of Supply</span>
                    <span className="text-[13px] font-medium text-slate-700">{order.place_of_supply || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                {/* Items Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Order Items</h2>
                      <p className="text-[12px] text-slate-500">{order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''} in this order</p>
                    </div>
                  </div>
                </div>
                
                {/* Items Table */}
                {order.items?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Details</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tax</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {order.items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-5 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-5 py-4">
                              <p className="text-[14px] font-medium text-slate-800">{item.item_name}</p>
                              {item.hsn_code && (
                                <p className="text-[11px] text-slate-400 mt-0.5">HSN: {item.hsn_code}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-700">
                                {item.quantity} {item.unit}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              ₹{(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              {item.tax_rate > 0 ? `${item.tax_rate}%` : '—'}
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this order</p>
                  </div>
                )}

                {/* Notes Section */}
                {order.customer_notes && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Customer Notes</p>
                    <p className="text-[13px] text-amber-800">{order.customer_notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card - Premium Green */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 shadow-sm border border-emerald-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Order Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{(order.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {(order.order_discount || order.total_discount || 0) > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Discount</span>
                      <span className="text-red-500 font-medium">-₹{(order.order_discount || order.total_discount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-slate-800">₹{(order.tax_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {(order.shipping_charges || 0) > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Shipping</span>
                      <span className="text-slate-800">₹{(order.shipping_charges || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  {(order.adjustment || 0) !== 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Adjustment</span>
                      <span className="text-slate-800">₹{(order.adjustment || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-emerald-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[22px] font-bold text-emerald-600">
                        ₹{(order.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Status Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <CreditCard className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Status</span>
                    <span className="inline-flex items-center px-2.5 py-1 bg-amber-100 rounded-full text-[12px] font-semibold text-amber-700">
                      Unpaid
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Amount Due</span>
                    <span className="text-[14px] font-semibold text-slate-800">
                      ₹{(order.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Due Date</span>
                    <span className="text-[13px] font-medium text-slate-700">
                      {order.due_date ? formatDate(order.due_date) : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={handleDownloadPdf}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors duration-200 text-left"
                  >
                    <Download className="w-5 h-5 text-slate-500" />
                    <span className="text-[13px] font-medium text-slate-700">Download PDF</span>
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingEmail ? <Loader2 className="w-5 h-5 text-slate-500 animate-spin" /> : <Mail className="w-5 h-5 text-slate-500" />}
                    <span className="text-[13px] font-medium text-slate-700">{sendingEmail ? 'Sending...' : 'Send via Email'}</span>
                  </button>
                  <button
                    onClick={handleSendWhatsApp}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors duration-200 text-left"
                  >
                    <MessageCircle className="w-5 h-5 text-emerald-600" />
                    <span className="text-[13px] font-medium text-emerald-700">Send via WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </>
  );
};

export default SalesOrderDetailPage;
